import type { PlanId } from "@biteasy/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";

const PLAN_DISPLAY_NAMES: Record<PlanId, string> = {
	bootstrap: "Bootstrap",
	standard: "Standard",
	scale: "Scale",
	enterprise: "Enterprise",
};

type ChangePlanDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	currentPlan: PlanId;
	targetPlan: "bootstrap" | "standard" | "scale";
};

/**
 * Dialog for upgrading or downgrading between paid plans.
 * Cancellation is handled by CancelSubscriptionDialog.
 */
export function ChangePlanDialog({
	isOpen,
	onClose,
	currentPlan,
	targetPlan,
}: ChangePlanDialogProps) {
	const queryClient = useQueryClient();

	const isUpgrade = getPlanRank(targetPlan) > getPlanRank(currentPlan);

	const { mutate: updatePlan, isPending } = useMutation(
		orpc.billing.updateSubscription.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getSubscription.key(),
				});
				toast.success(
					`Successfully ${isUpgrade ? "upgraded" : "changed"} to ${PLAN_DISPLAY_NAMES[targetPlan]}`,
				);
				onClose();
			},
			onError: (error) => {
				toast.error(error.message || "Failed to update subscription");
			},
		}),
	);

	const handleConfirm = () => {
		updatePlan({ plan: targetPlan });
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>
				{isUpgrade
					? `Upgrade to ${PLAN_DISPLAY_NAMES[targetPlan]}`
					: `Change to ${PLAN_DISPLAY_NAMES[targetPlan]}`}
			</DialogTitle>
			<DialogDescription>
				Change from {PLAN_DISPLAY_NAMES[currentPlan]} to{" "}
				{PLAN_DISPLAY_NAMES[targetPlan]}.
			</DialogDescription>
			<DialogBody>
				<Text>
					Your billing will be prorated. The change will take effect
					immediately.
				</Text>
			</DialogBody>
			<DialogActions>
				<Button plain onClick={onClose} disabled={isPending}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} disabled={isPending}>
					{isPending ? "Processing..." : "Confirm Change"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function getPlanRank(plan: PlanId): number {
	const ranks: Record<PlanId, number> = {
		bootstrap: 1,
		standard: 2,
		scale: 3,
		enterprise: 4,
	};
	return ranks[plan];
}
